// ========================================
// UNIFIED CASE MANAGEMENT SYSTEM V3
// Streamlined interface with unified case history and filtering
// Features: single table view, case-specific actions, dropdown creation
// ========================================

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  LinearProgress,
  Grid,
  IconButton,
  Menu,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  Support as SupportIcon,
  HelpOutline as HelpOutlineIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  caseManagementService, 
  CaseData, 
  CaseFilters, 
  CaseStatus, 
  CaseManagementService 
} from '../../services/caseManagementService';
import CustomerCaseCreationForm from './customer/CustomerCaseCreationForm';
import ModernSupplierCaseCreationForm from './supplier/ModernSupplierCaseCreationForm';
import { WorkflowCaseDashboard } from './WorkflowCaseDashboard';

interface CaseStats {
  total: number;
  active: number; // new + open + in_progress + pending_response
  resolved: number;
  closed: number;
  by_type: {
    supplier_case: number;
    customer_order: number;
    support: number;
    general: number;
  };
}

const UnifiedCaseManagement: React.FC = () => {
  const { showNotification } = useNotification();
  
  // Data state
  const [allCases, setAllCases] = useState<CaseData[]>([]);
  const [filteredCases, setFilteredCases] = useState<CaseData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [caseStats, setCaseStats] = useState<CaseStats>({
    total: 0,
    active: 0,
    resolved: 0,
    closed: 0,
    by_type: {
      supplier_case: 0,
      customer_order: 0,
      support: 0,
      general: 0
    }
  });

  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  // Filter state
  const [filters, setFilters] = useState<CaseFilters>({
    case_type: '',
    status: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Action state
  const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
  const [dashboardOpen, setDashboardOpen] = useState<boolean>(false);

  // Creation state
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [createCaseDialogOpen, setCreateCaseDialogOpen] = useState<boolean>(false);
  const [createSupplierCaseDialogOpen, setCreateSupplierCaseDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    loadAllCases();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [allCases, filters]);

  const loadAllCases = async () => {
    try {
      setLoading(true);
      
      const response = await caseManagementService.getCases({
        limit: 1000 // Load all cases for filtering
      });
      
      console.log('🔍 UnifiedCaseManagement getCases response:', response);
      if (response.data && response.data.length > 0) {
        console.log('🔍 First case from getCases:', response.data[0]);
        console.log('🔍 case_sub_type of first case:', response.data[0].case_sub_type);
      }
      
      if (response.success && response.data) {
        setAllCases(response.data);
        calculateStats(response.data);
      } else {
        showNotification('Fehler beim Laden der Fälle: ' + response.message, 'error');
      }
    } catch (error: any) {
      showNotification('Fehler beim Laden der Fälle: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const refreshCases = async () => {
    try {
      setRefreshing(true);
      await loadAllCases();
      showNotification('Fälle erfolgreich aktualisiert', 'success');
    } catch (error: any) {
      showNotification('Fehler beim Aktualisieren der Fälle: ' + error.message, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  const calculateStats = (cases: CaseData[]) => {
    const activeStatuses = ['new', 'open', 'in_progress', 'pending_response'];
    const stats: CaseStats = {
      total: cases.length,
      active: cases.filter(c => activeStatuses.includes(c.status)).length,
      resolved: cases.filter(c => c.status === 'resolved').length,
      closed: cases.filter(c => c.status === 'closed').length,
      by_type: {
        supplier_case: cases.filter(c => c.case_type === 'supplier_case').length,
        customer_order: cases.filter(c => c.case_type === 'customer_order').length,
        support: cases.filter(c => c.case_type === 'support').length,
        general: cases.filter(c => c.case_type === 'general').length
      }
    };
    setCaseStats(stats);
  };

  const applyFilters = () => {
    let filtered = [...allCases];

    // Apply case type filter
    if (filters.case_type) {
      filtered = filtered.filter(c => c.case_type === filters.case_type);
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(c => c.status === filters.status);
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(c => 
        (c.case_number?.toLowerCase().includes(searchLower)) ||
        (c.customer_email?.toLowerCase().includes(searchLower)) ||
        (c.customer_name?.toLowerCase().includes(searchLower)) ||
        (c.customer_company?.toLowerCase().includes(searchLower))
      );
    }

    setFilteredCases(filtered);
    setPage(0); // Reset pagination when filters change
  };

  const handleCaseCreated = async (newCase: any) => {
    setCreateCaseDialogOpen(false);
    setCreateSupplierCaseDialogOpen(false);
    setCreateMenuAnchor(null);
    showNotification(`Fall ${newCase.case_number || newCase.id} erfolgreich erstellt!`, 'success');
    await loadAllCases();
  };

  const handleCreateCaseTypeSelect = (caseType: string) => {
    setCreateMenuAnchor(null);
    
    if (caseType === 'supplier_case') {
      setCreateSupplierCaseDialogOpen(true);
    } else {
      setCreateCaseDialogOpen(true);
    }
  };

  const handleCaseAction = (caseData: CaseData, action: string) => {
    setSelectedCase(caseData);
    
    if (action === 'manage' || action === 'view') {
      // New WorkflowCaseDashboard handles both view and manage in one interface
      setDashboardOpen(true);
    } else if (action === 'close') {
      handleCloseCase(caseData);
    }
  };

  const handleCloseCase = async (caseData: CaseData) => {
    try {
      const response = await caseManagementService.executeCaseAction(caseData.id, 'close');
      if (response.success) {
        showNotification(`Fall ${caseData.case_number} erfolgreich geschlossen!`, 'success');
        await loadAllCases();
      } else {
        showNotification(response.message || 'Fehler beim Schließen des Falls', 'error');
      }
    } catch (error) {
      showNotification('Fehler beim Schließen des Falls', 'error');
    }
  };

  const getCaseActions = (caseData: CaseData) => {
    const baseActions = [
      { id: 'view', label: 'Anzeigen', icon: <VisibilityIcon /> }
    ];

    // Add case-specific actions
    if (caseData.case_type === 'supplier_case') {
      baseActions.push(
        { id: 'manage', label: 'Verwalten', icon: <SettingsIcon /> }
      );
    }

    // Add manage action for customer_order cases (Phase 3: Manual Export & Preprocessing)
    if (caseData.case_type === 'customer_order') {
      baseActions.push(
        { id: 'manage', label: 'Verwalten', icon: <SettingsIcon /> }
      );
    }

    // Add close case action if not already closed
    if (caseData.status !== 'closed') {
      baseActions.push(
        { id: 'close', label: 'Fall Schließen', icon: <CloseIcon /> }
      );
    }

    return baseActions;
  };

  const getCaseStatusChip = (status: CaseStatus) => {
    const statusInfo = CaseManagementService.getCaseStatusInfo(status);
    
    return (
      <Chip
        label={statusInfo.label}
        size="small"
        sx={{
          backgroundColor: statusInfo.color,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  };

  const getCaseTypeChip = (caseType: string) => {
    const typeInfo = CaseManagementService.getCaseTypeInfo(caseType);
    const colorMap: { [key: string]: any } = {
      'supplier_case': 'primary',
      'customer_order': 'secondary',
      'support': 'error',
      'general': 'info'
    };
    
    return (
      <Chip
        label={typeInfo.label}
        color={colorMap[caseType] || 'default'}
        size="small"
      />
    );
  };

  const formatDate = (dateString: string) => {
    return CaseManagementService.formatDate(dateString);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCases = filteredCases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.75rem' }}>
              {title}
            </Typography>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color, opacity: 0.7 }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Lade Fälle...
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Fall-Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
          >
            Filter
          </Button>
          <Button
            startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={refreshCases}
            disabled={refreshing}
            variant="outlined"
          >
            Aktualisieren
          </Button>
          <Button
            startIcon={<AddIcon />}
            endIcon={<ArrowDownIcon />}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            variant="contained"
          >
            Neuer Fall
          </Button>
        </Box>
      </Box>

      {/* Compact Statistics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Gesamt"
            value={caseStats.total}
            icon={<AssignmentIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Aktiv"
            value={caseStats.active}
            icon={<TrendingUpIcon />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Gelöst"
            value={caseStats.resolved}
            icon={<CheckCircleIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Geschlossen"
            value={caseStats.closed}
            icon={<ScheduleIcon />}
            color="text.secondary"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Fall-Typ</InputLabel>
                  <Select
                    value={filters.case_type || ''}
                    onChange={(e) => setFilters({ ...filters, case_type: e.target.value })}
                    label="Fall-Typ"
                  >
                    <MenuItem value="">Alle Typen</MenuItem>
                    <MenuItem value="supplier_case">Lieferanten</MenuItem>
                    <MenuItem value="customer_order">Kunden</MenuItem>
                    <MenuItem value="support">Support</MenuItem>
                    <MenuItem value="general">Allgemein</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="">Alle Status</MenuItem>
                    <MenuItem value="new">Neu</MenuItem>
                    <MenuItem value="open">Offen</MenuItem>
                    <MenuItem value="in_progress">In Bearbeitung</MenuItem>
                    <MenuItem value="pending_response">Wartet auf Antwort</MenuItem>
                    <MenuItem value="resolved">Gelöst</MenuItem>
                    <MenuItem value="closed">Geschlossen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  fullWidth
                  label="Suchen"
                  value={filters.search || ''}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Fall-Nr., E-Mail, Name, Firma..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Unified Case Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fall-Nr.</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Erstellt</TableCell>
                <TableCell>Kontakt</TableCell>
                <TableCell>Zugewiesen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCases.map((case_) => (
                <TableRow key={case_.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {case_.case_number || case_.id.slice(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getCaseTypeChip(case_.case_type)}
                  </TableCell>
                  <TableCell>
                    {getCaseStatusChip(case_.status as CaseStatus)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(case_.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {case_.customer_email || 'Nicht angegeben'}
                    </Typography>
                    {case_.customer_name && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {case_.customer_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {case_.assigned_to || 'Nicht zugewiesen'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1}>
                      {getCaseActions(case_).map((action) => (
                        <Tooltip key={action.id} title={action.label}>
                          <IconButton
                            size="small"
                            onClick={() => handleCaseAction(case_, action.id)}
                          >
                            {action.icon}
                          </IconButton>
                        </Tooltip>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredCases.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Create Case Menu */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={() => setCreateMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleCreateCaseTypeSelect('customer_order')}>
          <AssignmentIcon sx={{ mr: 2 }} />
          Kundenbestellung
        </MenuItem>
        <MenuItem onClick={() => handleCreateCaseTypeSelect('supplier_case')}>
          <BusinessIcon sx={{ mr: 2 }} />
          Lieferantenanfrage
        </MenuItem>
        <MenuItem onClick={() => handleCreateCaseTypeSelect('support')}>
          <SupportIcon sx={{ mr: 2 }} />
          Support-Fall
        </MenuItem>
        <MenuItem onClick={() => handleCreateCaseTypeSelect('general')}>
          <HelpOutlineIcon sx={{ mr: 2 }} />
          Allgemeine Anfrage
        </MenuItem>
      </Menu>

      {/* Workflow Case Dashboard */}
      <WorkflowCaseDashboard
        open={dashboardOpen}
        onClose={() => setDashboardOpen(false)}
        selectedCase={selectedCase}
        onCaseUpdated={loadAllCases}
      />

      {/* Case Creation Forms */}
      <CustomerCaseCreationForm
        open={createCaseDialogOpen}
        onClose={() => setCreateCaseDialogOpen(false)}
        onCaseCreated={handleCaseCreated}
      />
      
      <ModernSupplierCaseCreationForm
        open={createSupplierCaseDialogOpen}
        onClose={() => setCreateSupplierCaseDialogOpen(false)}
        onCaseCreated={handleCaseCreated}
      />
    </Box>
  );
};

export default UnifiedCaseManagement;
